import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useBack, useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, RED, GREEN, BORD, CARD, SUNK, DARK, MUTED, DIM, TEXT, num } from '../stockShared';

// Stock → Switches (the director, 28 Oct 2026; migration 085): material off
// by weight as parts are moulded, pallets recorded packed, work done outside,
// and the check when the lorry leaves -- "build everything and have you test
// it in sandbox, and then have everything ready for when I give it a go".
// Each does nothing until switched on here; each preview runs on the real
// data and writes nothing, so the figures can be checked against the floor
// first.

const ORDER = ['sold', 'material', 'pallets', 'outside', 'dispatch'];
// What switching each on or off does, said in the confirmation (29 Oct 2026:
// "no touching except if really intentional").
const EFFECT = {
  sold: { on: 'every run that ends of a part you sell as it is puts its good parts on the shelf.', off: 'runs of the parts you sell as they are no longer go onto the shelf; what they put there stays.' },
  material: { on: 'every run that ends from now takes its material off stock, by weight.', off: 'runs that end from now take no material off stock.' },
  pallets: { on: "packing a product (Made) stops taking its pallet, film, cover sheet and pallet label; they come off when someone records Pallet packed.", off: 'packing a product (Made) takes its share of the pallet packing again, as before, and Pallet packed is no longer offered.' },
  outside: { on: 'Sent out and Back are offered for the steps done outside, and what is sent shows as at the company.', off: 'Sent out and Back are no longer offered; what is recorded as at the company stays.' },
  dispatch: { on: 'every invoice issued from now is checked against Stock, and you are told on the bell when something on the lorry is not taken off.', off: 'invoices issued from now are not checked; lorries already checked keep their list.' },
};
const day = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const td = { padding: '6px 10px', borderTop: '1px solid #232a36', verticalAlign: 'top', fontSize: 13 };
const tdr = { ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const th = (right) => ({ padding: '7px 10px', textAlign: right ? 'right' : 'left', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: MUTED, fontWeight: 700 });
const box = { overflowX: 'auto', border: `1px solid ${BORD}`, borderRadius: 10, background: SUNK, marginTop: 8 };

export default function StockSwitches() {
  const goBack = useBack('/stock');
  const { toast } = useToast();
  const phone = useWindowWidth() < 760;
  const [sw, setSw] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(null);     // the switch whose on/off waits for "yes"
  const [open, setOpen] = useState({});           // previews shown
  const [prev, setPrev] = useState({});           // previews read
  const [invoice, setInvoice] = useState('');
  const [outside, setOutside] = useState(null);
  const [askingAll, setAskingAll] = useState(null); // true: everything on; false: everything off

  const load = useCallback(() => {
    api.get('/api/v1/stock/auto').then(r => { setSw(r.data.switches); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'The switches could not be read just now. Check the connection and reload.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const preview = useCallback((key, inv) => {
    setPrev(p => ({ ...p, [key]: { loading: true } }));
    const ask = key === 'material' ? api.get('/api/v1/stock/auto/preview/material', { params: { days: 30 } })
      : key === 'pallets' ? api.get('/api/v1/stock/auto/preview/pallets')
      : key === 'dispatch' ? api.get('/api/v1/stock/auto/preview/dispatch', { params: inv ? { invoice: inv } : {} })
      : api.get('/api/v1/stock');
    ask.then(r => {
      if (key === 'outside') setOutside(r.data.items.filter(i => i.active !== false && i.done_where === 'outside'));
      setPrev(p => ({ ...p, [key]: { data: r.data } }));
    }).catch(err => setPrev(p => ({ ...p, [key]: { error: err.response?.data?.error || 'The preview could not be worked out just now. Try again.' } })));
  }, []);
  const toggle = (key) => {
    setOpen(o => ({ ...o, [key]: !o[key] }));
    if (!prev[key]) preview(key);
  };

  async function flip(key, on) {
    try {
      const { data } = await api.put(`/api/v1/stock/auto/${key}`, { on });
      setSw(data.switches);
      setAsking(null);
      toast.success(on ? 'Switched on. It works from now on; what happened before is left as it is.' : 'Switched off. Nothing it wrote is taken back.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be switched just now. Try again.');
    }
  }
  async function flipAll(on) {
    try {
      const { data } = await api.put('/api/v1/stock/auto', { on });
      setSw(data.switches);
      setAskingAll(null);
      toast.success(on ? 'Automatic stock is on. It works from now on; what happened before is left as it is.' : 'Automatic stock is off. Nothing it wrote is taken back.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be switched just now. Try again.');
    }
  }

  const byKey = Object.fromEntries((sw || []).map(s => [s.key, s]));
  const keys = ORDER.filter(k => byKey[k]);       // the ones this server has
  const onCount = keys.filter(k => byKey[k].on).length;
  const allOn = !!sw && keys.length > 0 && onCount === keys.length;
  const lastOn = allOn ? keys.map(k => byKey[k].since).sort().pop() : null;
  return (
    <div style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 8 }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 4 }} />Stock
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ color: RED, fontSize: 18 }} aria-hidden="true" />Automatic stock usage
      </h1>
      <p style={{ color: DIM, fontSize: 13, margin: '0 0 16px', maxWidth: 780, lineHeight: 1.5 }}>
        Each of these keeps stock by itself once switched on, so switch one on only when its counts are right. Look at its preview first: it uses your real data and changes nothing. Switched on, it works from that moment; switched off, it stops from that moment. What it has already done stays either way.
      </p>
      {error && <div style={{ color: AMBER, fontSize: 13 }}>{error}</div>}
      {!sw && !error && <div style={{ color: MUTED, fontSize: 13 }}>Reading…</div>}
      {sw && (
        // One switch for all of them (the director, 3 Nov 2026: "once I have
        // all things counted correctly, I can switch it on and it would start
        // counting it automatically for all parts/products that it can").
        <section style={{ background: CARD, border: `1px solid ${allOn ? `${GREEN}88` : `${RED}66`}`, borderRadius: 14, padding: 16, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Automatic stock</h2>
                <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 6, padding: '1px 8px', color: allOn ? GREEN : onCount ? AMBER : DIM, border: `1px solid ${allOn ? GREEN : onCount ? AMBER : '#374151'}66`, background: allOn ? `${GREEN}14` : 'transparent' }}>
                  {allOn ? `On since ${day(lastOn)}` : onCount ? `${onCount} of ${keys.length} on` : 'Off'}
                </span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: DIM, lineHeight: 1.55, maxWidth: 780 }}>
                One switch for the five below. Switched on, stock keeps itself from that moment, for every part and product it can: runs of the parts you sell as they are go onto the shelf, material comes off by weight, pallets come off as they are packed, work sent outside is followed, and every lorry is checked against Stock.
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: MUTED, lineHeight: 1.55, maxWidth: 780 }}>
                Already working and not part of this switch: runs of the parts on your products' lists go onto the shelf, and Made takes off what goes into a product. Putting things together stays recorded by hand with Made, since the app cannot see when it is done.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {onCount > 0 && (
                <button type="button" onClick={() => setAskingAll(false)}
                  style={{ background: 'transparent', color: DIM, border: '1px solid #374151', borderRadius: 9, padding: '8px 14px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui' }}>
                  Switch everything off…
                </button>
              )}
              {!allOn && (
                <button type="button" onClick={() => setAskingAll(true)}
                  style={{ background: 'transparent', color: AMBER, border: `1px solid ${AMBER}99`, borderRadius: 9, padding: '8px 14px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui' }}>
                  Switch everything on…
                </button>
              )}
            </div>
          </div>
        </section>
      )}
      {sw && <h3 style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: MUTED, fontWeight: 700, margin: '0 0 8px' }}>One at a time</h3>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sw && keys.map(key => {
          const s = byKey[key];
          return (
            <section key={key} style={{ background: CARD, border: `1px solid ${s.on ? `${GREEN}66` : BORD}`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{s.label}</h2>
                    <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 6, padding: '1px 8px', color: s.on ? GREEN : DIM, border: `1px solid ${s.on ? GREEN : '#374151'}66`, background: s.on ? `${GREEN}14` : 'transparent' }}>
                      {s.on ? `On since ${day(s.since)}` : 'Off'}
                    </span>
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: DIM, lineHeight: 1.5, maxWidth: 760 }}>{s.does}</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => toggle(key)} aria-expanded={!!open[key]}
                    style={{ background: 'transparent', color: TEAL, border: `1px solid ${TEAL}88`, borderRadius: 9, padding: '7px 13px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' }}>
                    {open[key] ? 'Hide the preview' : 'Preview'}
                  </button>
                  <button type="button" onClick={() => setAsking(key)}
                    style={{ background: 'transparent', color: s.on ? DIM : AMBER, border: `1px solid ${s.on ? '#374151' : `${AMBER}99`}`, borderRadius: 9, padding: '7px 13px', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui' }}>
                    {s.on ? 'Switch off…' : 'Switch on…'}
                  </button>
                </div>
              </div>
              {open[key] && (
                <div style={{ marginTop: 10 }}>
                  {prev[key]?.loading && <div style={{ color: MUTED, fontSize: 13 }}>Working it out on your data…</div>}
                  {prev[key]?.error && <div style={{ color: AMBER, fontSize: 13 }}>{prev[key].error}</div>}
                  {prev[key]?.data && key === 'material' && <MaterialPreview d={prev[key].data} />}
                  {prev[key]?.data && key === 'pallets' && <PalletPreview d={prev[key].data} />}
                  {prev[key]?.data && key === 'outside' && <OutsidePreview items={outside || []} />}
                  {prev[key]?.data && key === 'sold' && <SoldPreview items={prev[key].data.items || []} />}
                  {prev[key]?.data && key === 'dispatch' && (
                    <DispatchPreview d={prev[key].data} invoice={invoice} onPick={(v) => { setInvoice(v); preview('dispatch', v); }} />
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
      {asking && byKey[asking] && (
        <Confirm s={byKey[asking]} onYes={() => flip(asking, !byKey[asking].on)} onNo={() => setAsking(null)} />
      )}
      {askingAll !== null && sw && (
        <ConfirmAll on={askingAll} switches={keys.map(k => byKey[k]).filter(s => s.on !== askingAll)}
          onYes={() => flipAll(askingAll)} onNo={() => setAskingAll(null)} />
      )}
    </div>
  );
}

// Everything on or off at once: what each one still to change will do, and --
// switching on -- what is not counted yet, since "once I have all things
// counted correctly" is when it is meant to go on. "No" is the one ready to press.
function ConfirmAll({ on, switches, onYes, onNo }) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onNo(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onNo]);
  useEffect(() => {
    if (!on) return;
    api.get('/api/v1/stock').then(r => setItems(r.data.items || [])).catch(() => setItems(false));
  }, [on]);
  const uncounted = items ? items.filter(i => i.active !== false && !i.level?.counted) : [];
  return (
    <div onClick={onNo} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true" aria-labelledby="confirm-all-title"
        style={{ background: CARD, border: `1px solid ${RED}88`, borderRadius: 14, maxWidth: 580, width: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: '18px 20px', fontFamily: 'system-ui', color: TEXT, boxSizing: 'border-box' }}>
        <div id="confirm-all-title" style={{ fontSize: 17, fontWeight: 800, display: 'flex', gap: 10, alignItems: 'center' }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ color: RED }} aria-hidden="true" />
          {on ? 'Switch on automatic stock?' : 'Switch off automatic stock?'}
        </div>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: '12px 0 6px' }}>From the moment you switch {on ? 'it on' : 'it off'}:</p>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.55 }}>
          {switches.map(s => <li key={s.key} style={{ marginBottom: 4 }}><b>{s.label}</b>: {EFFECT[s.key][on ? 'on' : 'off']}</li>)}
        </ul>
        {on && (
          <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.55 }}>
            {items === null && <span style={{ color: MUTED }}>Looking at what is counted…</span>}
            {items === false && <span style={{ color: AMBER }}>What is counted could not be read just now. Check Stock for items marked "not counted yet" before switching on.</span>}
            {items && !uncounted.length && <span style={{ color: GREEN }}>Everything in Stock has been counted.</span>}
            {items && uncounted.length > 0 && (
              <>
                <div style={{ color: AMBER, fontWeight: 700 }}>{uncounted.length} item{uncounted.length === 1 ? ' is' : 's are'} not counted yet.</div>
                <div style={{ color: DIM }}>
                  {uncounted.length === 1 ? 'It starts' : 'Each starts'} from 0 until it is counted, so anything taken off it shows below 0. A count puts it right whenever it is done. Count {uncounted.length === 1 ? 'it' : 'them'} first, or switch on now and count {uncounted.length === 1 ? 'it' : 'them'} when you can.
                </div>
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', color: TEAL }}>Which ones</summary>
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    {uncounted.map(i => <Link key={i.id} to={`/stock/${i.id}`} style={{ color: TEXT, fontSize: 12.5 }}>{i.name}</Link>)}
                  </div>
                </details>
              </>
            )}
          </div>
        )}
        <p style={{ fontSize: 13, lineHeight: 1.55, color: DIM, margin: '12px 0 0' }}>
          {on
            ? 'Nothing before now is caught up. You can switch it off again at any time, all of it or one at a time; what it has taken off by then stays.'
            : 'Nothing it has already taken off is put back. Runs of the parts on your products\' lists still go onto the shelf, as before.'}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18, flexWrap: 'wrap' }}>
          <button type="button" autoFocus onClick={onNo}
            style={{ background: 'transparent', color: TEXT, border: '1px solid #374151', borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' }}>
            No, leave it as it is
          </button>
          <button type="button" disabled={busy} onClick={async () => { setBusy(true); await onYes(); setBusy(false); }}
            style={{ background: RED, color: '#0f172a', border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 800, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'system-ui' }}>
            {on ? 'Yes, switch everything on' : 'Yes, switch everything off'}
          </button>
        </div>
      </div>
    </div>
  );
}

// The parts sold as they are: moulded, on no product's list, their own
// "Production adds to it" off -- what the switch would start feeding.
function SoldPreview({ items }) {
  const sold = items.filter(i => i.kind === 'moulded' && i.part_id != null && !i.from_production && !(i.products || []).length);
  const fed = items.filter(i => i.kind === 'moulded' && i.from_production).length;
  if (!sold.length) return <div style={{ fontSize: 13, color: DIM }}>No part is sold as it is: every moulded part is on a product's list, and its runs already go onto the shelf.</div>;
  return (
    <>
      <div style={{ fontSize: 12.5, color: DIM }}>
        The {sold.length} parts you sell as they are. Switched on, every run of them that ends puts its good parts here; the check when the lorry leaves takes them off. Runs of the {fed} parts on your products' lists already go onto the shelf.
      </div>
      <div style={box}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
          <thead><tr><th style={th()}>Part</th><th style={th(true)}>On the shelf</th><th style={th()}>Counted</th></tr></thead>
          <tbody>
            {sold.map(i => (
              <tr key={i.id}><td style={td}><Link to={`/stock/${i.id}`} style={{ color: TEXT }}>{i.name}</Link></td>
                <td style={tdr}>{num(i.level?.total || 0)}</td>
                <td style={{ ...td, color: i.level?.counted ? DIM : AMBER }}>{i.level?.counted ? new Date(i.level.last_count).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'not counted yet'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// The confirmation: what switching it on or off does, from when, and that
// what it has already done stays. "No" is the one ready to press.
function Confirm({ s, onYes, onNo }) {
  const turningOn = !s.on;
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onNo(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onNo]);
  return (
    <div onClick={onNo} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"
        style={{ background: CARD, border: `1px solid ${RED}88`, borderRadius: 14, maxWidth: 520, width: '100%', padding: '18px 20px', fontFamily: 'system-ui', color: TEXT, boxSizing: 'border-box' }}>
        <div id="confirm-title" style={{ fontSize: 17, fontWeight: 800, display: 'flex', gap: 10, alignItems: 'center' }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ color: RED }} aria-hidden="true" />
          {turningOn ? `Switch on "${s.label}"?` : `Switch off "${s.label}"?`}
        </div>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: '12px 0 0' }}>
          From the moment you {turningOn ? 'switch it on' : 'switch it off'}, {EFFECT[s.key][turningOn ? 'on' : 'off']}
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: DIM, margin: '8px 0 0' }}>
          {turningOn
            ? 'Nothing before now is caught up. You can switch it off again at any time; what it has taken off by then stays, and a count or a correction puts a figure right.'
            : 'Nothing it has already taken off is put back. You can switch it on again at any time; it then works from that moment, and the time it was off is not caught up.'}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18, flexWrap: 'wrap' }}>
          <button type="button" autoFocus onClick={onNo}
            style={{ background: 'transparent', color: TEXT, border: '1px solid #374151', borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' }}>
            No, leave it {s.on ? 'on' : 'off'}
          </button>
          <button type="button" disabled={busy} onClick={async () => { setBusy(true); await onYes(); setBusy(false); }}
            style={{ background: RED, color: '#0f172a', border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 800, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'system-ui' }}>
            {turningOn ? 'Yes, switch it on' : 'Yes, switch it off'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialPreview({ d }) {
  const stuck = d.jobs.filter(j => !j.lines.length);
  return (
    <>
      <div style={{ fontSize: 12.5, color: DIM }}>
        The {d.jobs.length} runs that ended in the last {d.days} days, and the material each would have taken off: the good parts' own weight, shared as the recipe says. Nothing was taken off; this is only worked out.
      </div>
      <div style={box}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
          <thead><tr><th style={th()}>Material</th><th style={th(true)}>Would come off</th><th style={th(true)}>On hand now</th></tr></thead>
          <tbody>
            {d.totals.map(t => (
              <tr key={t.id}><td style={td}>{t.name}</td><td style={tdr}><b>{num(t.qty)} {t.unit}</b></td>
                <td style={{ ...tdr, color: !t.counted ? AMBER : t.on_hand < t.qty ? AMBER : DIM }}>{t.counted ? `${num(t.on_hand)} ${t.unit}` : 'not counted yet'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: TEAL }}>Run by run</summary>
        <div style={box}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
            <thead><tr><th style={th()}>Run</th><th style={th(true)}>Good</th><th style={th(true)}>Each</th><th style={th()}>Would take off</th></tr></thead>
            <tbody>
              {d.jobs.filter(j => j.lines.length).map(j => (
                <tr key={j.job}>
                  <td style={td}>{j.part}<div style={{ fontSize: 11.5, color: MUTED }}>job {j.job} · ended {new Date(j.ended_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{j.recipe ? ` · ${j.recipe}` : ''}</div></td>
                  <td style={tdr}>{num(j.good)}</td>
                  <td style={tdr}>{num(j.g)} g<div style={{ fontSize: 11, color: MUTED, whiteSpace: 'normal' }}>{j.how}</div></td>
                  <td style={td}>{j.lines.map(l => `${num(l.qty)} ${l.unit} ${l.name}`).join(' + ')}{j.missing.length ? <div style={{ color: AMBER, fontSize: 12 }}>Not counted: {j.missing.join('; ')}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      {stuck.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: AMBER, lineHeight: 1.5 }}>
          Could not be worked out: {[...new Map(stuck.map(j => [j.part, j.why])).entries()].map(([p, w]) => `${p} (${w})`).join('; ')}. Fill them in Parts and they are counted.
        </div>
      )}
    </>
  );
}

function PalletPreview({ d }) {
  const gaps = d.items.filter(i => !i.per_pallet || i.cannot.length);
  return (
    <>
      <div style={{ fontSize: 12.5, color: DIM }}>What one full pallet of each takes off when it is recorded packed, from Admin → Packaging. The boxes and their tape still come off when the product is made.</div>
      <div style={box}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
          <thead><tr><th style={th()}>Packed product, or part sold as it is</th><th style={th(true)}>On a pallet</th><th style={th()}>One pallet takes</th></tr></thead>
          <tbody>
            {d.items.filter(i => i.per_pallet).map(i => (
              <tr key={i.id}><td style={td}>{i.name}</td><td style={tdr}>{num(i.per_pallet)}</td>
                <td style={td}>{i.take.map(t => `${num(t.qty)}${t.unit && t.unit !== 'kos' ? ` ${t.unit}` : ''} ${t.name}`).join(' · ')}
                  {i.cannot.length ? <div style={{ color: AMBER, fontSize: 12 }}>Not in Stock yet: {i.cannot.map(c => c.name).join(', ')}</div> : null}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      {gaps.filter(i => !i.per_pallet).length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: AMBER }}>No pallet in Admin → Packaging: {gaps.filter(i => !i.per_pallet).map(i => i.name).join(', ')}.</div>
      )}
    </>
  );
}

function OutsidePreview({ items }) {
  if (!items.length) return <div style={{ fontSize: 13, color: DIM }}>No step is done outside yet. In a product's Build steps, set "where it is done" to an outside company and it shows here.</div>;
  return (
    <>
      <div style={{ fontSize: 12.5, color: DIM }}>The steps done by an outside company. Switched on, each gets Sent out and Back when recording, and on its build step.</div>
      <div style={box}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
          <thead><tr><th style={th()}>Step</th><th style={th()}>Done by</th><th style={th(true)}>On the shelf</th><th style={th(true)}>At the company</th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id}><td style={td}><Link to={`/stock/${i.id}`} style={{ color: TEXT }}>{i.name}</Link></td><td style={td}>{i.done_by || 'an outside company (not named)'}</td>
                <td style={tdr}>{num(i.level?.total || 0)}</td><td style={tdr}>{num(i.level?.away || 0)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export const STATE = {
  done: { label: 'Taken off', color: GREEN },
  missing: { label: 'Not taken off', color: AMBER },
  not_from_stock: { label: 'Not from stock', color: DIM },
  unknown: { label: 'Not in Stock', color: AMBER },
  unit: { label: 'Other unit', color: AMBER },
};
export const stateChip = (s) => {
  const x = STATE[s] || STATE.missing;
  return <span style={{ fontSize: 11, fontWeight: 700, color: x.color, border: `1px solid ${x.color}66`, background: `${x.color}14`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>{x.label}</span>;
};
export function DispatchRows({ rows, actions }) {
  return (
    <div style={box}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 680 }}>
        <thead><tr><th style={th()}>On the lorry</th><th style={th(true)}>Needed</th><th style={th(true)}>Off stock</th><th style={th()}>State</th>{actions && <th style={th()} />}</tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key}>
              <td style={td}>
                {r.what === 'pallets' ? <>Pallets of {r.item?.name}</> : (r.item?.name || r.description)}
                <div style={{ fontSize: 11.5, color: MUTED }}>{r.what === 'pallets' ? 'their pallet, film, cover sheet, tape and label' : `invoice line: ${r.description}`}{r.why ? ` · ${r.why}` : ''}</div>
                {r.decision && <div style={{ fontSize: 11.5, color: MUTED }}>Marked not from stock by {r.decision.by || 'a director'}</div>}
              </td>
              <td style={tdr}>{num(r.need)}{r.what === 'pallets' ? (r.need === 1 ? ' pallet' : ' pallets') : ''}</td>
              <td style={tdr}>{num(r.done + (r.waiting || 0))}{r.waiting ? <div style={{ fontSize: 11, color: MUTED }}>{num(r.waiting)} packed, waiting</div> : null}</td>
              <td style={td}>{stateChip(r.state)}</td>
              {actions && <td style={{ ...td, whiteSpace: 'nowrap' }}>{actions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DispatchPreview({ d, invoice, onPick }) {
  if (!d.invoice) return <div style={{ fontSize: 13, color: DIM }}>{d.why}</div>;
  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: DIM }}>
        <span>The check as it would come out for</span>
        <select value={invoice || d.invoice.id} onChange={e => onPick(e.target.value)}
          style={{ background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '5px 8px', fontFamily: 'system-ui' }}>
          {(d.choices || []).map(c => <option key={c.id} value={c.id}>{c.number}{c.dispatch_date ? ` · left ${c.dispatch_date}` : ''}</option>)}
        </select>
        <span>with pallets recorded packed. Nothing is taken off; this is only worked out.</span>
      </div>
      <DispatchRows rows={d.rows} />
      <div style={{ marginTop: 8, fontSize: 12.5, color: d.clear ? GREEN : AMBER }}>
        {d.clear ? 'All clear: nothing would be listed.' : `${d.open} thing${d.open === 1 ? '' : 's'} would be listed on the bell, each with Take off now and Not from stock.`}
      </div>
    </>
  );
}
